import { useContext } from 'react';
import { commonAction } from '../types/commonActions';
import { createContext } from './createContext';
import type { ToastProps } from '@/globalComponents/toast';
import noop from '@/utils/noop';

export type Login = {
  open: boolean;
  redirectTo?: string;
  message?: string;
  callback?: Function;
};

export type AppConfigStateTypes = {
  config: {
    serviceCost?: number;
    [key: string]: any;
  };
  footer?: any;
  search?: boolean;
  login: Login;
  saleAssist: boolean;
  hamburger: boolean;
  toast: ToastProps;
  header: {
    nav: any[];
    featured: any[];
    profile: any[];
  };
  isFlutterClient?: boolean;
};

export type AppConfigDispatchType = {
  updateAppConfig: Function;
  updateServiceCost: (cost: number) => void;
  toggleSearch: (d?: any) => void;
  toggleSaleAssist: (d?: any) => void;
  toggleLoginModal: (d?: any, title?: string, cb?: Function) => void;
  toggleHamburger: (d?: any) => void;
  triggerToast: (d?: any) => void;
};

const APP_CONTEXT_ACTION_TYPES = {
  UPDATE: 'UPDATE',
  UPDATE_SERVICE_COST: 'UPDATE_SERVICE_COST',
  TOGGLE_SEARCH: 'TOGGLE_SEARCH',
  TOGGLE_LOGIN_MODAL: 'TOGGLE_LOGIN_MODAL',
  TOGGLE_SALE_ASSIST: 'TOGGLE_SALE_ASSIST',
  TOGGLE_HAMBURGER: 'TOGGLE_HAMBURGER',
  TRIGGER_TOAST: 'TRIGGER_TOAST',
};

export const TOAST_INITIAL_STATE = {
  message: '',
  show: false,
  onClose: noop,
};

const INITIAL_STATE = {
  search: false,
  saleAssist: false,
  login: {
    open: false,
  },
  toast: TOAST_INITIAL_STATE,
  hamburger: false,
  config: {
    serviceCost: 0,
  },
  header: {
    nav: [],
    featured: [],
    profile: [],
  },
};

const reducer = (
  state: AppConfigStateTypes,
  { type, payload }: commonAction,
): AppConfigStateTypes => {
  switch (type) {
    case APP_CONTEXT_ACTION_TYPES.UPDATE:
      return { ...state, ...payload };
    case APP_CONTEXT_ACTION_TYPES.UPDATE_SERVICE_COST:
      return {
        ...state,
        config: {
          ...state.config,
          serviceCost: payload,
        },
      };
    case APP_CONTEXT_ACTION_TYPES.TOGGLE_SEARCH:
      return { ...state, search: !state.search };
    case APP_CONTEXT_ACTION_TYPES.TOGGLE_SALE_ASSIST:
      return { ...state, saleAssist: payload };
    case APP_CONTEXT_ACTION_TYPES.TOGGLE_LOGIN_MODAL:
      return {
        ...state,
        login: {
          open: !state.login.open,
          redirectTo: !state.login.open ? payload.href : undefined,
          message: !state.login.open ? payload.message : undefined,
          callback: !state.login.open ? payload.callback : undefined,
        },
      };
    case APP_CONTEXT_ACTION_TYPES.TOGGLE_HAMBURGER:
      return { ...state, hamburger: !state.hamburger };
    case APP_CONTEXT_ACTION_TYPES.TRIGGER_TOAST:
      return { ...state, toast: payload };
    default:
      return state;
  }
};

const updateAppConfig = (dispatch: any) => (config: any) =>
  dispatch({
    type: APP_CONTEXT_ACTION_TYPES.UPDATE,
    payload: {
      config,
    },
  });

const updateServiceCost = (dispatch: any) => (cost: number) =>
  dispatch({
    type: APP_CONTEXT_ACTION_TYPES.UPDATE_SERVICE_COST,
    payload: cost,
  });

const toggleSearch = (dispatch: any) => () =>
  dispatch({
    type: APP_CONTEXT_ACTION_TYPES.TOGGLE_SEARCH,
  });

const toggleSaleAssist = (dispatch: any) => (state: boolean) =>
  dispatch({
    type: APP_CONTEXT_ACTION_TYPES.TOGGLE_SALE_ASSIST,
    payload: state,
  });

const toggleLoginModal =
  (dispatch: any) => (href?: string, message?: string, callback?: Function) =>
    dispatch({
      type: APP_CONTEXT_ACTION_TYPES.TOGGLE_LOGIN_MODAL,
      payload: {
        href,
        message,
        callback,
      },
    });

const toggleHamburger = (dispatch: any) => () =>
  dispatch({
    type: APP_CONTEXT_ACTION_TYPES.TOGGLE_HAMBURGER,
  });

const triggerToast = (dispatch: any) => (payload: ToastProps) =>
  dispatch({
    type: APP_CONTEXT_ACTION_TYPES.TRIGGER_TOAST,
    payload,
  });

const { Context: AppContext, Provider: AppContextProvider } = createContext<
  AppConfigStateTypes,
  AppConfigDispatchType
>(
  reducer,
  {
    updateAppConfig,
    updateServiceCost,
    toggleSearch,
    toggleSaleAssist,
    toggleLoginModal,
    toggleHamburger,
    triggerToast,
  },
  INITIAL_STATE,
);

AppContext.displayName = 'App';
export const useAppConfigContext = () => useContext(AppContext);

export { AppContext, AppContextProvider };
